import { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'
import { ErrorAction, normalizeAxiosError, pushException } from '../../reducers/errorReducer'
import { Destination, isGraphError, isSpringBootError } from '../../context/AxiosContext'
import AuthorizationContext, { DestinationAuthorityMap, OfficeTokenPayload } from '../../context/AuthorizationContext'
import useAxios from '../../hooks/useAxios'
import { useDispatch } from 'react-redux'
import { useContext, useEffect, useRef } from 'react'
import { jwtDecode } from 'jwt-decode'

export default function useTokenErrorHandler() {
    const axios = useAxios()
    const dispatch = useDispatch()

    const props = useContext(AuthorizationContext)
    const updateTokensRef = useRef(props.updateTokens)

    // Keep refs in sync with latest context values
    useEffect(() => {
        updateTokensRef.current = props.updateTokens
    }, [props.updateTokens])

    return (error: AxiosError) =>
        (resolve: (value: AxiosResponse) => void, reject: (error?: AxiosError) => void): void => {
            // This condition is hit when an expired bearer token is sent along with a back-end request. We
            // get here because MSAL does only renew access tokens with acquireTokenSilent(), but not ID
            // tokens. IDX10223 is the code included in the message for token lifetime expiry in Azure AD.
            const isInvalid =
                isGraphError(error) && error.response?.status === 401 && error.response?.data?.error?.code === 'InvalidAuthenticationToken'
            const isExpiredMsal = isSpringBootError(error) && error.response?.status === 401 && error.response?.data?.message?.includes('IDX10223')
            const isExpiredAuth0 = isSpringBootError(error) && error.response?.status === 401 && error.response?.data?.message?.includes('The Token')
            const isHammockUnauthorized =
                error.response?.status === 401 && error.response?.data === '' && error.config?.headers?.['Destination'] === Destination.Hammock
            const isMissing = !error.config?.headers?.hasOwnProperty('Authorization')

            if (
                (isInvalid || isExpiredMsal || isExpiredAuth0 || isMissing || isHammockUnauthorized) &&
                error.config?.headers?.['Destination'] &&
                !error.config?.headers?.['IsRerun']
            ) {
                //Fetch a new token and retry the request. This often fixes errors when the browser aborts the refresh timeouts.
                const authority = DestinationAuthorityMap.get(error.config?.headers?.['Destination'])

                updateTokensRef
                    .current?.(authority)
                    .then(token => {
                        if (token?.access_token) {
                            const decodedToken = jwtDecode<OfficeTokenPayload>(token?.access_token)
                            console.debug('AuthorizationProvider: useTokenErrorHandler: new token:', decodedToken.uti)
                        }

                        // Make sure we only get here once per unique request by setting the IsRerun property.
                        // This prevents us from getting stuck in an endless loop if we can't get a valid token.
                        const config: AxiosRequestConfig = {
                            ...error.config,
                            headers: {
                                ...(error.config.headers || {}),
                                IsRerun: String(true),
                                Authorization: `${token.token_type} ${token.access_token}`,
                            },
                        }
                        return axios.request(config).then(resolve).catch(reject)
                    })
                    .catch(reject)
            } else {
                if (!error.config?.headers?.SuppressErrors && error?.message !== 'canceled') {
                    if (error.response && error.config?.headers?.Destination === Destination.Hammock) {
                        // Resolve any pending network error, because we received a valid response from the backend,
                        // albeit it being an error. Only do this when we're not suppressing errors, because we
                        // don't want to trigger global state updates.
                        dispatch({ type: ErrorAction.DismissNetworkError })
                    }
                    if (error.response?.status === 401) {
                        error.message =
                            'Wegens veiligheidsredenen ben u uitgelogd. Log opnieuw in door HAMMOCK te herstarten via de Support knop onderin. Excuses voor het ongemak.'
                    }
                    dispatch(pushException(normalizeAxiosError(error), error))
                }
                // We need to reject the error so that it will not propagate as a successful call.
                reject(error)
            }
        }
}
